import { useEffect, useState } from "react";
import { api, inr } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Plus, Download } from "lucide-react";
import { toast } from "sonner";
import Attachments from "../components/Attachments";
import Comments from "../components/Comments";

const STATUSES = ["not_started", "rfq_sent", "po_pending", "po_raised", "ordered", "in_transit", "partially_received", "received", "dispatched", "installed", "delayed", "cancelled"];
const PAYMENT_STATUSES = ["unpaid", "advance_paid", "part_paid", "paid"];
const empty = {
  item_name: "", vendor: "", vendor_contact: "", project_id: "",
  boq_line: "", category: "", brand: "", model: "",
  quantity_required: 0, quantity: 1, quantity_received: 0, quantity_dispatched: 0, quantity_installed: 0,
  cost: 0, selling_price: null,
  po_number: "", po_date: "", order_date: "", expected_delivery: "", actual_delivery: "",
  status: "not_started", delay_reason: "",
  payment_status: "unpaid", warranty_details: "", notes: "",
};

const statusColor = {
  not_started: "bg-slate-500/20 text-slate-300 border-slate-500/30",
  rfq_sent: "bg-slate-400/20 text-slate-300 border-slate-400/30",
  po_pending: "bg-slate-500/20 text-slate-300 border-slate-500/30",
  po_raised: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  ordered: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  in_transit: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  partially_received: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30",
  received: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  dispatched: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30",
  installed: "bg-primary/20 text-primary border-primary/30",
  delayed: "bg-red-500/20 text-red-300 border-red-500/30",
  cancelled: "bg-slate-700/40 text-slate-400 border-slate-600/40",
};

export default function ProcurementPage() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/procurement").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/projects").then((r) => setProjects(r.data)); }, []);

  const submit = async () => {
    try {
      if (editId) await api.patch(`/procurement/${editId}`, form);
      else await api.post("/procurement", form);
      setOpen(false); load(); toast.success("Saved");
    } catch (e) { toast.error("Failed"); }
  };

  const csv = () => {
    const rows = items.map((i) => ({ item: i.item_name, vendor: i.vendor, qty: i.quantity, cost: i.cost, status: i.status, order_date: i.order_date, eta: i.expected_delivery }));
    if (!rows.length) return;
    const keys = Object.keys(rows[0]);
    const data = [keys.join(","), ...rows.map((r) => keys.map((k) => `"${r[k] ?? ""}"`).join(","))].join("\n");
    const blob = new Blob([data], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "procurement.csv"; a.click();
  };

  return (
    <div className="space-y-4" data-testid="procurement-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Supply Chain</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Procurement</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={csv}><Download className="h-4 w-4 mr-2" />CSV</Button>
          <Button onClick={() => { setEditId(null); setForm(empty); setOpen(true); }} data-testid="new-procurement-btn"><Plus className="h-4 w-4 mr-2" />New Item</Button>
        </div>
      </div>

      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Item</TableHead><TableHead>Vendor</TableHead><TableHead>Project</TableHead>
            <TableHead>Qty</TableHead><TableHead>Cost</TableHead>
            <TableHead>Ordered</TableHead><TableHead>ETA</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id} className="cursor-pointer" onClick={() => { setEditId(i.id); setForm({ ...i }); setOpen(true); }} data-testid={`procurement-row-${i.id}`}>
                <TableCell className="font-medium">{i.item_name}</TableCell>
                <TableCell>{i.vendor || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{projects.find((p) => p.id === i.project_id)?.client_name || "—"}</TableCell>
                <TableCell className="font-mono">{i.quantity}</TableCell>
                <TableCell className="font-mono text-primary">{inr(i.cost)}</TableCell>
                <TableCell className="font-mono text-xs">{i.order_date || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{i.expected_delivery || "—"}</TableCell>
                <TableCell><Badge className={`${statusColor[i.status]} border capitalize`}>{i.status.replace("_", " ")}</Badge></TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No procurement items.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="procurement-dialog">
          <DialogHeader><DialogTitle>{editId ? "Edit Procurement Item" : "New Procurement Item"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <PField label="Item Name" required className="col-span-2 md:col-span-3">
              <Input placeholder="e.g. Trinnov Altitude 32" value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} />
            </PField>
            <PField label="Project">
              <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="none">No project</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.client_name}</SelectItem>)}</SelectContent>
              </Select>
            </PField>
            <PField label="BOQ Line Item"><Input placeholder="e.g. Line #12" value={form.boq_line || ""} onChange={(e) => setForm({ ...form, boq_line: e.target.value })} /></PField>
            <PField label="Category"><Input placeholder="e.g. Processor" value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} /></PField>

            <PField label="Brand"><Input placeholder="e.g. Trinnov" value={form.brand || ""} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></PField>
            <PField label="Model"><Input placeholder="e.g. Altitude 32" value={form.model || ""} onChange={(e) => setForm({ ...form, model: e.target.value })} /></PField>
            <PField label="Vendor"><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} /></PField>

            <PField label="Vendor Contact"><Input placeholder="Phone / email" value={form.vendor_contact || ""} onChange={(e) => setForm({ ...form, vendor_contact: e.target.value })} /></PField>

            <PField label="Qty Required"><Input type="number" min="0" value={form.quantity_required || 0} onChange={(e) => setForm({ ...form, quantity_required: Number(e.target.value) })} /></PField>
            <PField label="Qty Ordered"><Input type="number" min="0" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })} /></PField>
            <PField label="Qty Received"><Input type="number" min="0" value={form.quantity_received || 0} onChange={(e) => setForm({ ...form, quantity_received: Number(e.target.value) })} /></PField>
            <PField label="Qty Dispatched"><Input type="number" min="0" value={form.quantity_dispatched || 0} onChange={(e) => setForm({ ...form, quantity_dispatched: Number(e.target.value) })} /></PField>
            <PField label="Qty Installed"><Input type="number" min="0" value={form.quantity_installed || 0} onChange={(e) => setForm({ ...form, quantity_installed: Number(e.target.value) })} /></PField>

            <PField label="Purchase Cost (₹)"><Input type="number" min="0" value={form.cost} onChange={(e) => setForm({ ...form, cost: Number(e.target.value) })} /></PField>
            <PField label="Selling Price (₹)" hint="Optional — admin-only visible"><Input type="number" min="0" value={form.selling_price ?? ""} onChange={(e) => setForm({ ...form, selling_price: e.target.value === "" ? null : Number(e.target.value) })} /></PField>

            <PField label="PO Number"><Input placeholder="e.g. TAV/PO/26/145" value={form.po_number || ""} onChange={(e) => setForm({ ...form, po_number: e.target.value })} /></PField>
            <PField label="PO Date"><Input type="date" value={form.po_date || ""} onChange={(e) => setForm({ ...form, po_date: e.target.value })} /></PField>
            <PField label="Order Date"><Input type="date" value={form.order_date || ""} onChange={(e) => setForm({ ...form, order_date: e.target.value })} /></PField>
            <PField label="Expected Delivery"><Input type="date" value={form.expected_delivery || ""} onChange={(e) => setForm({ ...form, expected_delivery: e.target.value })} /></PField>
            <PField label="Actual Delivery"><Input type="date" value={form.actual_delivery || ""} onChange={(e) => setForm({ ...form, actual_delivery: e.target.value })} /></PField>
            <PField label="Payment Status">
              <Select value={form.payment_status || "unpaid"} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </PField>

            <PField label="Status" className="col-span-2 md:col-span-3">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="procurement-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </PField>

            {form.status === "delayed" && (
              <PField label="Delay Reason" className="col-span-2 md:col-span-3">
                <Input placeholder="Why is it delayed?" value={form.delay_reason || ""} onChange={(e) => setForm({ ...form, delay_reason: e.target.value })} />
              </PField>
            )}

            <PField label="Warranty Details" className="col-span-2 md:col-span-3">
              <Input placeholder="e.g. 2 years on-site, extended 5 years" value={form.warranty_details || ""} onChange={(e) => setForm({ ...form, warranty_details: e.target.value })} />
            </PField>
            <PField label="Notes" className="col-span-2 md:col-span-3">
              <Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </PField>
          </div>
          {editId && (
            <>
              <div className="pt-3 border-t border-border">
                <Attachments kind="procurement" parentId={editId} />
              </div>
              <div className="pt-3 border-t border-border">
                <Comments entity="procurement" parentId={editId} />
              </div>
            </>
          )}
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit}>Save</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PField({ label, hint, required, children, className }) {
  return (
    <div className={className}>
      <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[10.5px] text-muted-foreground/70 mt-1">{hint}</div>}
    </div>
  );
}
